// The only browser transport for GA4. Never pass auth/Stripe objects to this module.
export const ANALYTICS_CONSENT_KEY = 'symuliga_cookie_consent_v1';
export const ANALYTICS_CONSENT_EVENT = 'symuliga:analytics-consent';
export const ANALYTICS_SETTINGS_EVENT = 'symuliga:analytics-settings';
export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-N9B6NT3LBT';
const CONTEXT_KEY = 'symuliga_sales_context_v1';
const TOKEN_KEY = 'symuliga_analytics_consent_token_v1';
const REVOCATIONS_KEY = 'symuliga_pending_analytics_revocations_v1';
const CONTEXT_TTL_MS = 24 * 60 * 60 * 1000;
const EVENTS = new Set(['page_view', 'paywall_view', 'vip_page_view', 'vip_offer_view',
  'vip_cta_click', 'checkout_view', 'begin_checkout', 'checkout_error',
  'checkout_cancel', 'login', 'sign_up']);
export const FEATURES = new Set(['home', 'league', 'team', 'scenario_lab', 'own_simulation',
  'team_charts', 'fixture_difficulty', 'position_matrix', 'playoff', 'team_comparisons',
  'summary', 'fixtures', 'points_threshold', 'standings_calculator', 'elo_ranking',
  'odds', 'article', 'blog', 'navigation', 'vip', 'checkout', 'account', 'other']);
const TOOL_FEATURES = {
  conditions: 'scenario_lab', yourSimulation: 'own_simulation', wcYourSimulation: 'own_simulation',
  fixture_difficulty: 'fixture_difficulty', positionMatrix: 'position_matrix',
  wcPositionMatrix: 'position_matrix', playoff: 'playoff', 'team-comparisons': 'team_comparisons',
  summary: 'summary', wcSummary: 'summary', fixtures: 'fixtures',
  'points-threshold': 'points_threshold', wcPointThreshold: 'points_threshold',
  standingsCalculator: 'standings_calculator', WcStandingsCalculator: 'standings_calculator',
  'elo-ranking': 'elo_ranking', wcMatchesProbabilities: 'odds', leagueTables: 'league',
};
const PARAMS = new Set(['feature', 'source_feature', 'source_path', 'competition_id',
  'league_level', 'region_id', 'requirement', 'placement', 'auth_state', 'access_tier',
  'page_type', 'plan', 'billing_period', 'currency', 'value', 'error_code', 'method']);
const ENUM_PARAMS = {
  page_type: FEATURES, auth_state: new Set(['anonymous', 'logged_in']),
  access_tier: new Set(['free', 'vip', 'admin']), plan: new Set(['vip']),
  billing_period: new Set(['monthly', 'yearly']), currency: new Set(['PLN']), method: new Set(['email']),
  placement: new Set(['navigation', 'content', 'paywall', 'vip_page', 'demo', 'article', 'plan_card']),
  requirement: new Set(['free', 'login', 'vip', 'poland', 'poland_regions']),
};
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_id', 'utm_source_platform'];
let memoryConsent;
let memoryContext = null;
let memoryToken = null;
let initialized = false;
let currentContext = {};
let pageLocation = '';
let pageReferrer = '';

const readStorage = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const writeStorage = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* Consent and tracking continue in memory if storage is unavailable. */ }
};

export function analyticsEnabled() {
  if (process.env.REACT_APP_ANALYTICS_ENABLED === 'false') return false;
  const productionHost = ['symuliga.pl', 'www.symuliga.pl'].includes(window.location.hostname);
  // A test environment must explicitly opt in and use a different stream.
  return productionHost || (process.env.REACT_APP_ANALYTICS_ENABLED === 'true'
    && GA_MEASUREMENT_ID !== 'G-N9B6NT3LBT');
}

export function readAnalyticsConsent() {
  return memoryConsent || readStorage(ANALYTICS_CONSENT_KEY);
}

export function hasAnalyticsConsent() {
  return analyticsEnabled() && readAnalyticsConsent() === 'analytics';
}

export function analyticsPath(value = window.location.pathname) {
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return '/other';
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const league = path.match(/^\/liga\/(\d+)(?:\/([^/]+))?$/);
    if (league) return `/liga/${league[1]}${TOOL_FEATURES[league[2]] ? `/${league[2]}` : ''}`;
    const team = path.match(/^\/team\/(\d+)(?:\/([^/]+))?$/);
    if (team) return `/team/${team[1]}${team[2] === 'analytics' ? '/analytics' : ''}`;
    if (/^\/blog\/[a-z0-9-]{1,90}$/.test(path)) return path;
    if (/^\/dokumenty\/[a-z0-9-]{1,60}$/.test(path)) return path;
    if (['/', '/vip', '/kup-vip', '/login', '/register', '/blog', '/odds', '/elo-ranking',
      '/billing/success', '/billing/cancel', '/konto/subskrypcja', '/dokumenty',
      '/achievements-list', '/simulation-results', '/pstryk', '/narzedzia'].includes(path)) return path;
    return '/other';
  } catch { return '/other'; }
}

export function pageContext(pathname, competitions = {}) {
  const path = analyticsPath(pathname);
  const league = path.match(/^\/liga\/(\d+)(?:\/([^/]+))?$/);
  let feature = 'other';
  if (league) feature = TOOL_FEATURES[league[2]] || 'league';
  else if (/^\/team\/\d+\/analytics$/.test(path)) feature = 'team_charts';
  else if (/^\/team\//.test(path)) feature = 'team';
  else if (/^\/blog\//.test(path)) feature = 'article';
  else feature = ({ '/': 'home', '/blog': 'blog', '/vip': 'vip', '/kup-vip': 'checkout',
    '/odds': 'odds', '/elo-ranking': 'elo_ranking', '/konto/subskrypcja': 'account' })[path] || 'other';
  const result = { feature, source_feature: feature, source_path: path, page_type: feature };
  if (league) {
    result.competition_id = Number(league[1]);
    const competition = Object.values(competitions || {}).flat()
      .find((item) => Number(item.competition_id) === result.competition_id);
    if (competition) {
      if (Number(competition.level) > 0) result.league_level = Number(competition.level);
      if (Number(competition.region_id) > 0) result.region_id = Number(competition.region_id);
    }
  }
  return result;
}

export function cleanAnalyticsParams(params) {
  const result = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (!PARAMS.has(key) || value === null || value === undefined) return;
    if (key === 'source_path') { result[key] = analyticsPath(value); return; }
    if (key === 'feature' || key === 'source_feature') {
      if (FEATURES.has(value)) result[key] = value;
      return;
    }
    if (ENUM_PARAMS[key]) {
      if (ENUM_PARAMS[key].has(value)) result[key] = value;
      return;
    }
    if (key === 'error_code') {
      if (/^(http_[1-5]\d{2}|checkout_failed)$/.test(value)) result[key] = value;
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0
      && (key === 'value' || (Number.isSafeInteger(value) && value > 0))) result[key] = value;
  });
  return result;
}

const landingCampaign = (() => {
  const campaign = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (CAMPAIGN_PARAMS.includes(key) && /^[a-zA-Z0-9_.-]{1,80}$/.test(value)) campaign[key] = value;
  });
  return campaign;
})();

export function safePageLocation(value = window.location.href) {
  const url = new URL(value, window.location.origin);
  const query = new URLSearchParams();
  CAMPAIGN_PARAMS.forEach((key) => {
    const entry = url.searchParams.get(key);
    if (entry && /^[a-zA-Z0-9_.-]{1,80}$/.test(entry)) query.set(key, entry);
  });
  const queryString = query.toString();
  return `${window.location.origin}${analyticsPath(url.pathname)}${queryString ? `?${queryString}` : ''}`;
}

function safeReferrer(value) {
  if (!value) return '';
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol)) return '';
    return url.origin === window.location.origin ? safePageLocation(value) : `${url.origin}/`;
  } catch { return ''; }
}

export function initializeAnalytics() {
  if (!hasAnalyticsConsent()) return false;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  if (initialized) return true;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied',
    ad_user_data: 'denied', ad_personalization: 'denied' });
  window.gtag('consent', 'update', { analytics_storage: 'granted' });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    page_location: safePageLocation(),
    page_referrer: safeReferrer(document.referrer),
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(landingCampaign.utm_source ? { campaign_source: landingCampaign.utm_source } : {}),
    ...(landingCampaign.utm_medium ? { campaign_medium: landingCampaign.utm_medium } : {}),
    ...(landingCampaign.utm_campaign ? { campaign_name: landingCampaign.utm_campaign } : {}),
    ...(landingCampaign.utm_content ? { campaign_content: landingCampaign.utm_content } : {}),
    ...(landingCampaign.utm_id ? { campaign_id: landingCampaign.utm_id } : {}),
    ...(process.env.REACT_APP_GA_DEBUG === 'true' ? { debug_mode: true } : {}),
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);
  return true;
}

export function setAnalyticsConsent(value) {
  const tokenToRevoke = memoryToken || readStorage(TOKEN_KEY);
  memoryConsent = value === 'analytics' ? 'analytics' : 'necessary';
  writeStorage(ANALYTICS_CONSENT_KEY, memoryConsent);
  if (memoryConsent === 'analytics') {
    if (initialized) {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
  } else {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    if (initialized) window.gtag('consent', 'update', { analytics_storage: 'denied' });
    memoryContext = null;
    memoryToken = null;
    writeStorage(CONTEXT_KEY, null);
    writeStorage(TOKEN_KEY, null);
    pageLocation = '';
    if (tokenToRevoke && /^[a-f0-9]{64}$/.test(tokenToRevoke)) {
      let pending = [];
      try { pending = JSON.parse(readStorage(REVOCATIONS_KEY)) || []; } catch { /* Ignore damaged storage. */ }
      if (!Array.isArray(pending)) pending = [];
      writeStorage(REVOCATIONS_KEY, JSON.stringify([...new Set([...pending, tokenToRevoke])].slice(-10)));
      flushConsentRevocations();
    }
  }
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
}

export function syncAnalyticsConsent() {
  memoryConsent = undefined;
  memoryToken = null;
  memoryContext = null;
  const granted = hasAnalyticsConsent();
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = !granted;
  if (initialized) window.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
  if (!granted) pageLocation = '';
}

export async function flushConsentRevocations() {
  // Retrying withdrawal is a consent preference operation; it never loads GA.
  let pending;
  try { pending = JSON.parse(readStorage(REVOCATIONS_KEY)) || []; } catch { return; }
  if (!Array.isArray(pending)) return;
  for (const token of pending) {
    if (!/^[a-f0-9]{64}$/.test(token)) continue;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/billing/analytics-consent/revoke`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consentToken: token }), keepalive: true,
      });
      if (!response.ok) continue;
      const latest = JSON.parse(readStorage(REVOCATIONS_KEY)) || [];
      writeStorage(REVOCATIONS_KEY, JSON.stringify(latest.filter((entry) => entry !== token)));
    } catch { /* Retry on the next visit/online event if the browser was offline. */ }
  }
}

export function setCurrentAnalyticsContext(context) {
  currentContext = cleanAnalyticsParams(context);
}

export function rememberSalesContext(context) {
  if (!hasAnalyticsConsent()) return;
  memoryContext = { params: cleanAnalyticsParams(context), expiresAt: Date.now() + CONTEXT_TTL_MS };
  writeStorage(CONTEXT_KEY, JSON.stringify(memoryContext));
}

export function readSalesContext() {
  if (!hasAnalyticsConsent()) return {};
  try {
    const stored = memoryContext || JSON.parse(readStorage(CONTEXT_KEY));
    if (stored && stored.expiresAt > Date.now()) return cleanAnalyticsParams(stored.params);
  } catch { /* An invalid/expired context must not break navigation. */ }
  return {};
}

export function trackAnalyticsEvent(name, params = {}, transport = {}) {
  if (!EVENTS.has(name) || !initializeAnalytics()) return false;
  window.gtag('event', name, { ...cleanAnalyticsParams({ ...currentContext, ...params }),
    page_location: pageLocation || safePageLocation(), page_referrer: pageReferrer,
    send_to: GA_MEASUREMENT_ID, ...transport });
  return true;
}

export function trackAnalyticsPage(path) {
  if (!initializeAnalytics()) return;
  pageReferrer = pageLocation || safeReferrer(document.referrer);
  pageLocation = safePageLocation(path);
  const title = `Symuliga — ${currentContext.page_type || 'other'}`;
  window.gtag('set', { page_location: pageLocation, page_referrer: pageReferrer, page_title: title });
  trackAnalyticsEvent('page_view');
}

function getConsentToken() {
  if (memoryToken) return memoryToken;
  const saved = readStorage(TOKEN_KEY);
  if (saved && /^[a-f0-9]{64}$/.test(saved)) { memoryToken = saved; return saved; }
  if (!window.crypto?.getRandomValues) return null;
  memoryToken = Array.from(window.crypto.getRandomValues(new Uint8Array(32)),
    (byte) => byte.toString(16).padStart(2, '0')).join('');
  writeStorage(TOKEN_KEY, memoryToken);
  return memoryToken;
}

function getGaValue(field) {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), 700);
    window.gtag('get', GA_MEASUREMENT_ID, field, (value) => {
      window.clearTimeout(timeout);
      resolve(value);
    });
  });
}

export async function checkoutAnalyticsContext() {
  if (!initializeAnalytics()) return null;
  const [clientId, sessionId] = await Promise.all([getGaValue('client_id'), getGaValue('session_id')]);
  if (!hasAnalyticsConsent()) return null;
  const consentToken = getConsentToken();
  if (!hasAnalyticsConsent() || !consentToken || !/^\d{1,20}\.\d{1,20}$/.test(String(clientId))
    || !/^\d{1,12}$/.test(String(sessionId))) return null;
  return { analyticsConsent: true, clientId: String(clientId), sessionId: String(sessionId),
    consentToken, context: cleanAnalyticsParams({ ...readSalesContext(), ...currentContext }) };
}

export function trackBeginCheckout(billingPeriod) {
  const params = { plan: 'vip', billing_period: billingPeriod, currency: 'PLN' };
  return new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, 500);
    const sent = trackAnalyticsEvent('begin_checkout', params, {
      items: [{ item_id: `vip_${billingPeriod}`, item_name: 'Symuliga VIP',
        item_variant: billingPeriod, quantity: 1 }],
      event_callback: () => { window.clearTimeout(timeout); resolve(); }, event_timeout: 500,
    });
    if (!sent) { window.clearTimeout(timeout); resolve(); }
  });
}
